import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  FileImage,
  ImageOff,
  Link2,
  Pencil,
  ShieldAlert,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiClientError, api } from "../lib/api";
import { fileSize, integer, whenever } from "../lib/format";
import i18n from "../lib/i18n";
import { cn } from "../lib/utils";
import { LICENSE_VARIANT, type LicenseKind } from "./Inventory";

const LICENSE_KINDS: LicenseKind[] = ["original", "licensed", "public_domain", "unreviewed"];
const LICENSE_FILTERS: Array<LicenseKind | "all"> = ["all", ...LICENSE_KINDS];

const ACCEPTED_TYPES =
  "image/png,image/jpeg,image/webp,image/gif,image/svg+xml,application/pdf,application/postscript,application/illustrator";

interface Design {
  id: string;
  title: string;
  franchise: string | null;
  licenseKind: LicenseKind;
  licenseExpiresOn: string | null;
  licenseNote: string | null;
  mimeType: string;
  fileSizeBytes: number;
  productId: string | null;
  uploadedBy: string | null;
  createdAt: string;
  thumbnailUrl: string | null;
}

interface Summary {
  total: number;
  unreviewed: number;
  licensed: number;
  original: number;
  publicDomain: number;
  expiringSoon: number;
}

interface ProductOption {
  id: string;
  sku: string;
  title: string;
}

export default function Designs() {
  const { t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const qc = useQueryClient();

  const [filterLicense, setFilterLicense] = useState<LicenseKind | "all">("all");
  const [term, setTerm] = useState("");

  const invalidateDesigns = () => {
    void qc.invalidateQueries({ queryKey: ["designs"] });
    void qc.invalidateQueries({ queryKey: ["designs", "summary"] });
  };

  const configQ = useQuery({
    queryKey: ["designs", "config"],
    queryFn: () => api<{ storageConfigured: boolean }>("/api/v1/designs/config"),
  });
  const summaryQ = useQuery({
    queryKey: ["designs", "summary"],
    queryFn: () => api<Summary>("/api/v1/designs/summary"),
  });

  const params = new URLSearchParams({ limit: "100" });
  if (filterLicense !== "all") params.set("licenseKind", filterLicense);
  if (term.trim()) params.set("q", term.trim());

  const designsQ = useQuery({
    queryKey: ["designs", filterLicense, term],
    queryFn: () => api<Design[]>(`/api/v1/designs?${params.toString()}`),
  });

  // Best-effort: a designs-scoped user without inventory access shouldn't see the
  // whole page break over a 403 on the link-to-product dropdown — just disable it.
  const productsQ = useQuery({
    queryKey: ["products", "for-designs"],
    queryFn: async () => {
      try {
        return await api<ProductOption[]>("/api/v1/products?limit=200");
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 403) return [];
        throw err;
      }
    },
  });

  const rows = designsQ.data ?? [];
  const products = productsQ.data ?? [];
  const storageConfigured = configQ.data?.storageConfigured ?? false;

  /* ─────────────────────────── upload ─────────────────────────── */

  const [uploadOpen, setUploadOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<{ id: string; file: File }[]>([]);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadFranchise, setUploadFranchise] = useState("");
  const [uploadLicense, setUploadLicense] = useState<LicenseKind>("unreviewed");
  const [uploadExpires, setUploadExpires] = useState("");
  const [uploadNote, setUploadNote] = useState("");
  const [uploadProductId, setUploadProductId] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    succeeded: number;
    failed: { filename: string; error: string }[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) =>
      api<{ succeeded: Design[]; failed: { filename: string; error: string }[] }>(
        "/api/v1/designs",
        { method: "POST", body: formData },
      ),
  });

  function addFiles(files: FileList | File[]) {
    const withIds = Array.from(files).map((file) => ({ id: crypto.randomUUID(), file }));
    setSelectedFiles((prev) => [...prev, ...withIds]);
  }

  function removeFile(id: string) {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function resetUploadForm() {
    setSelectedFiles([]);
    setUploadTitle("");
    setUploadFranchise("");
    setUploadLicense("unreviewed");
    setUploadExpires("");
    setUploadNote("");
    setUploadProductId("");
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    setUploadError(null);
    setUploadResult(null);
    if (selectedFiles.length === 0) return;

    const formData = new FormData();
    for (const { file } of selectedFiles) formData.append("files", file);
    if (uploadTitle.trim()) formData.append("title", uploadTitle.trim());
    if (uploadFranchise.trim()) formData.append("franchise", uploadFranchise.trim());
    formData.append("licenseKind", uploadLicense);
    if (uploadExpires) formData.append("licenseExpiresOn", uploadExpires);
    if (uploadNote.trim()) formData.append("licenseNote", uploadNote.trim());
    if (uploadProductId) formData.append("productId", uploadProductId);

    try {
      const result = await uploadMutation.mutateAsync(formData);
      setUploadResult({ succeeded: result.succeeded.length, failed: result.failed });
      invalidateDesigns();
      if (result.failed.length === 0) {
        resetUploadForm();
        setUploadOpen(false);
      }
    } catch (err) {
      setUploadError(
        err instanceof ApiClientError ? err.message : t("designs:errors.uploadFailed"),
      );
    }
  }

  /* ─────────────────────────── inline edit ─────────────────────────── */

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFranchise, setEditFranchise] = useState("");
  const [editLicense, setEditLicense] = useState<LicenseKind>("unreviewed");
  const [editExpires, setEditExpires] = useState("");
  const [editNote, setEditNote] = useState("");
  const [editProductId, setEditProductId] = useState("");
  const [editError, setEditError] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api(`/api/v1/designs/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: () => {
      invalidateDesigns();
      setEditingId(null);
    },
    onError: (err) =>
      setEditError(err instanceof ApiClientError ? err.message : t("designs:errors.updateFailed")),
  });

  function openEdit(d: Design) {
    setEditingId(d.id);
    setEditError(null);
    setEditFranchise(d.franchise ?? "");
    setEditLicense(d.licenseKind);
    setEditExpires(d.licenseExpiresOn ? d.licenseExpiresOn.slice(0, 10) : "");
    setEditNote(d.licenseNote ?? "");
    setEditProductId(d.productId ?? "");
  }

  function submitEdit(id: string) {
    setEditError(null);
    updateMutation.mutate({
      id,
      body: {
        franchise: editFranchise.trim() || null,
        licenseKind: editLicense,
        licenseExpiresOn: editExpires || null,
        licenseNote: editNote.trim() || null,
        productId: editProductId || null,
      },
    });
  }

  /* ─────────────────────────── delete ─────────────────────────── */

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/api/v1/designs/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      invalidateDesigns();
      setDeletingId(null);
    },
    onError: (err) =>
      setDeleteError(
        err instanceof ApiClientError ? err.message : t("designs:errors.deleteFailed"),
      ),
  });

  return (
    <>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1>{t("navigation:designs")}</h1>
          <p className="mt-2 text-muted-foreground">{t("designs:subtitle")}</p>
        </div>
        <Button
          type="button"
          onClick={() => setUploadOpen((v) => !v)}
          disabled={!configQ.isLoading && !storageConfigured}
        >
          <Upload className="h-4 w-4" aria-hidden />
          {t("designs:upload.title")}
        </Button>
      </div>

      {!configQ.isLoading && !storageConfigured && (
        <Card className="mb-6 border-[hsl(var(--chart-3)/0.45)] bg-[hsl(var(--chart-3)/0.10)]">
          <CardContent className="flex items-start gap-3 p-4">
            <AlertTriangle
              className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--chart-3))]"
              aria-hidden
            />
            <div>
              <p className="font-medium">{t("designs:storageNotConfigured.title")}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t("designs:storageNotConfigured.body")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {uploadOpen && storageConfigured && (
        <Card className="mb-6">
          <CardHeader className="p-4 pb-2">
            <CardTitle className="text-base">{t("designs:upload.title")}</CardTitle>
            <CardDescription>{t("designs:upload.dragDropHint")}</CardDescription>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <form onSubmit={handleUpload} className="space-y-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
                }}
                className={cn(
                  "flex w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors",
                  dragActive ? "border-primary bg-accent" : "border-input",
                )}
              >
                <Upload className="h-6 w-6 text-muted-foreground" aria-hidden />
                <span className="text-sm text-muted-foreground">
                  {t("designs:upload.dragDropHint")}
                </span>
                <span className="text-sm font-medium underline">
                  {t("designs:upload.selectFiles")}
                </span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPTED_TYPES}
                className="sr-only"
                onChange={(e) => {
                  if (e.target.files?.length) addFiles(e.target.files);
                  e.target.value = "";
                }}
              />

              {selectedFiles.length > 0 && (
                <ul className="space-y-1">
                  {selectedFiles.map(({ id, file }) => (
                    <li
                      key={id}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    >
                      <span className="truncate">
                        {file.name}{" "}
                        <span className="text-muted-foreground">
                          ({fileSize(file.size, locale)})
                        </span>
                      </span>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        aria-label={t("designs:upload.remove")}
                        onClick={() => removeFile(id)}
                      >
                        <X className="h-4 w-4" aria-hidden />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {selectedFiles.length === 1 && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <Label htmlFor="upload-title">{t("designs:card.title")}</Label>
                    <Input
                      id="upload-title"
                      value={uploadTitle}
                      onChange={(e) => setUploadTitle(e.target.value)}
                      placeholder={selectedFiles[0]?.file.name}
                      className="mt-1"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="upload-franchise">{t("designs:upload.franchiseLabel")}</Label>
                  <Input
                    id="upload-franchise"
                    value={uploadFranchise}
                    onChange={(e) => setUploadFranchise(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="upload-license">{t("designs:upload.licenseKindLabel")}</Label>
                  <Select
                    value={uploadLicense}
                    onValueChange={(v) => setUploadLicense(v as LicenseKind)}
                  >
                    <SelectTrigger id="upload-license" className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LICENSE_KINDS.map((k) => (
                        <SelectItem key={k} value={k}>
                          {t(`inventory:license.${k}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {uploadLicense === "licensed" && (
                  <div>
                    <Label htmlFor="upload-expires">
                      {t("designs:upload.licenseExpiresLabel")}
                    </Label>
                    <Input
                      id="upload-expires"
                      type="date"
                      value={uploadExpires}
                      onChange={(e) => setUploadExpires(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                )}
                <div className="sm:col-span-2 lg:col-span-3">
                  <Label htmlFor="upload-note">{t("designs:upload.licenseNoteLabel")}</Label>
                  <textarea
                    id="upload-note"
                    value={uploadNote}
                    onChange={(e) => setUploadNote(e.target.value)}
                    rows={2}
                    className="mt-1 flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>
                {products.length > 0 && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <Label htmlFor="upload-product">{t("designs:upload.linkProductLabel")}</Label>
                    <Select value={uploadProductId} onValueChange={setUploadProductId}>
                      <SelectTrigger id="upload-product" className="mt-1 w-full">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.sku} — {p.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Button
                  type="submit"
                  disabled={selectedFiles.length === 0 || uploadMutation.isPending}
                >
                  {uploadMutation.isPending
                    ? t("designs:upload.uploading")
                    : t("designs:upload.submit")}
                </Button>
                <Button type="button" variant="outline" onClick={() => setUploadOpen(false)}>
                  {t("inventory:cancel")}
                </Button>
                <span className="text-sm text-muted-foreground">
                  {t("designs:upload.filesSelected", { count: selectedFiles.length })}
                </span>
              </div>

              {uploadError && <p className="text-sm text-destructive">{uploadError}</p>}
              {uploadResult && uploadResult.failed.length > 0 && (
                <div className="rounded-md border border-destructive/40 p-3 text-sm">
                  <p className="font-medium text-destructive">
                    {t("designs:upload.partialFailure", {
                      succeeded: uploadResult.succeeded,
                      failed: uploadResult.failed.length,
                    })}
                  </p>
                  <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                    {uploadResult.failed.map((f) => (
                      <li key={f.filename}>
                        {f.filename}: {f.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label={t("designs:stats.total")} value={integer(summaryQ.data?.total ?? 0, locale)} />
        <Stat
          label={t("designs:stats.unreviewed")}
          value={integer(summaryQ.data?.unreviewed ?? 0, locale)}
          warn={(summaryQ.data?.unreviewed ?? 0) > 0}
        />
        <Stat
          label={t("designs:stats.licensed")}
          value={integer(summaryQ.data?.licensed ?? 0, locale)}
        />
        <Stat
          label={t("designs:stats.original")}
          value={integer(summaryQ.data?.original ?? 0, locale)}
        />
        <Stat
          label={t("designs:stats.publicDomain")}
          value={integer(summaryQ.data?.publicDomain ?? 0, locale)}
        />
        <Stat
          label={t("designs:stats.expiringSoon")}
          value={integer(summaryQ.data?.expiringSoon ?? 0, locale)}
          warn={(summaryQ.data?.expiringSoon ?? 0) > 0}
          Icon={ShieldAlert}
        />
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex-1">
          <Label htmlFor="design-search" className="sr-only">
            {t("designs:filter.searchPlaceholder")}
          </Label>
          <Input
            id="design-search"
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={t("designs:filter.searchPlaceholder")}
          />
        </div>
        <fieldset className="flex flex-wrap gap-1 border-0 p-0">
          <legend className="sr-only">{t("designs:filter.all")}</legend>
          {LICENSE_FILTERS.map((k) => (
            <Button
              key={k}
              type="button"
              size="sm"
              variant={filterLicense === k ? "default" : "ghost"}
              onClick={() => setFilterLicense(k)}
            >
              {k === "all" ? t("designs:filter.all") : t(`inventory:license.${k}`)}
            </Button>
          ))}
        </fieldset>
      </div>

      {designsQ.isLoading && <p className="text-muted-foreground">{t("common:loading")}</p>}

      {!designsQ.isLoading && rows.length === 0 && (
        <p className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          {t("designs:empty.title")}
        </p>
      )}

      {deleteError && <p className="mb-3 text-sm text-destructive">{deleteError}</p>}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {rows.map((d) => {
          const linkedProduct = products.find((p) => p.id === d.productId);
          return (
            <Card key={d.id} className="overflow-hidden">
              <div className="flex aspect-square items-center justify-center bg-muted">
                {d.thumbnailUrl ? (
                  <img
                    src={d.thumbnailUrl}
                    alt={d.title}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <FileImage className="h-10 w-10 text-muted-foreground" aria-hidden />
                )}
              </div>
              <CardContent className="space-y-2 p-3">
                <p className="truncate font-medium" title={d.title}>
                  {d.title}
                </p>
                <span className="flex flex-wrap items-center gap-1.5">
                  {d.franchise && <Badge variant="outline">{d.franchise}</Badge>}
                  <Badge variant={LICENSE_VARIANT[d.licenseKind]}>
                    {t(`inventory:license.${d.licenseKind}`)}
                  </Badge>
                  {linkedProduct && (
                    <Badge variant="secondary">
                      <Link2 className="mr-1 inline h-3 w-3" aria-hidden />
                      {linkedProduct.sku}
                    </Badge>
                  )}
                </span>
                <p className="text-xs text-muted-foreground">
                  {fileSize(d.fileSizeBytes, locale)} · {whenever(d.createdAt, locale)}
                </p>
                {d.licenseExpiresOn && (
                  <p className="text-xs text-muted-foreground">
                    {t("designs:card.licenseExpires", {
                      date: whenever(d.licenseExpiresOn, locale),
                    })}
                  </p>
                )}

                {editingId === d.id ? (
                  <div className="space-y-2 border-t pt-2">
                    <Input
                      value={editFranchise}
                      onChange={(e) => setEditFranchise(e.target.value)}
                      placeholder={t("designs:upload.franchiseLabel")}
                    />
                    <Select
                      value={editLicense}
                      onValueChange={(v) => setEditLicense(v as LicenseKind)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LICENSE_KINDS.map((k) => (
                          <SelectItem key={k} value={k}>
                            {t(`inventory:license.${k}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {editLicense === "licensed" && (
                      <Input
                        type="date"
                        value={editExpires}
                        onChange={(e) => setEditExpires(e.target.value)}
                      />
                    )}
                    <textarea
                      value={editNote}
                      onChange={(e) => setEditNote(e.target.value)}
                      rows={2}
                      placeholder={t("designs:upload.licenseNoteLabel")}
                      className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                    {products.length > 0 && (
                      <Select value={editProductId} onValueChange={setEditProductId}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t("designs:card.linkProduct")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">{t("designs:card.unlinkProduct")}</SelectItem>
                          {products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.sku} — {p.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {editError && <p className="text-xs text-destructive">{editError}</p>}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={updateMutation.isPending}
                        onClick={() => submitEdit(d.id)}
                      >
                        {updateMutation.isPending
                          ? t("inventory:saving")
                          : t("designs:card.saveEdit")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingId(null)}
                      >
                        {t("inventory:cancel")}
                      </Button>
                    </div>
                  </div>
                ) : deletingId === d.id ? (
                  <div className="flex items-center gap-2 border-t pt-2">
                    <span className="text-xs text-muted-foreground">
                      {t("designs:card.deleteConfirm")}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      disabled={deleteMutation.isPending}
                      onClick={() => deleteMutation.mutate(d.id)}
                    >
                      {t("designs:card.delete")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setDeletingId(null)}
                    >
                      {t("inventory:cancel")}
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-1.5 border-t pt-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => openEdit(d)}>
                      <Pencil className="h-3.5 w-3.5" aria-hidden />
                      {t("designs:card.editLicense")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setDeleteError(null);
                        setDeletingId(d.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      {t("designs:card.delete")}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </>
  );
}

function Stat({
  label,
  value,
  warn,
  Icon,
}: {
  label: string;
  value: string;
  warn?: boolean;
  Icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card>
      <CardHeader className="p-3 pb-1">
        <CardTitle className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
          {Icon ? <Icon className="h-3.5 w-3.5" /> : <ImageOff className="h-3.5 w-3.5 opacity-0" />}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <span
          className={cn(
            "block text-2xl font-semibold tabular-nums",
            warn && "text-[hsl(var(--chart-3))]",
          )}
        >
          {value}
        </span>
      </CardContent>
    </Card>
  );
}
